import { SHIFTS, getShiftKey } from "@/constants/shifts";
import { packingItemKey, packingStorageKey, readPackingState, writePackingState } from "./packingStorage";

/**
 * Čeklista pakovanja po firmi i smeni: pakuje se i nosi po smeni, pa je stavka jelo u jednoj smeni
 * ("Pileća rolnica, smena 2"). Stanje (šta je spakovano i da li je firma "spremna") čuva se samo na ovom
 * uređaju (localStorage, po restoranu i periodu), isti ključ za telefon i računar. Izbor smena samo bira
 * koje se smene prikazuju, ne briše spakovano.
 * Zahteva nasleđen ReportMealsTabPackages (companies, selectedShifts, filters) i prop `shifts`.
 */
export default {
  data: () => ({
    packingState: {},
  }),
  computed: {
    isSingleDay() {
      return this.filters.dates.from === this.filters.dates.to;
    },
    storageKey() {
      const restaurantId = this.$store.state.restaurant?.id;
      const { from, to } = this.filters.dates;
      return packingStorageKey(restaurantId, from, to);
    },
    packingCompanies() {
      const search = this.filters.search;
      return this.companies
        .map((company) => this.toPackingCompany(company, search))
        .filter((company) => company.total > 0);
    },
  },
  watch: {
    storageKey: {
      handler() {
        this.packingState = this.readPackingState();
        this.onPackingStateReset();
      },
      immediate: true,
    },
  },
  methods: {
    /**
     * `groups`: stavke firme razvrstane po smeni (smena 1 → 4, redosled kojim se nosi);
     * svaka grupa ima svoj zbir i napredak.
     */
    toPackingCompany(company, search) {
      const key = company.name;
      const state = this.packingState[key] || { items: {}, ready: false };
      const saved = state.items || {};
      const companyMatches = !search || this.matches(company.name, search);

      const items = [];
      (company.meals || [])
        .filter((meal) => companyMatches || this.matches(meal.name, search))
        .forEach((meal) => {
          this.selectedShifts.forEach((shiftId) => {
            const slot = meal[getShiftKey(shiftId)];
            const count = slot?.totalQuantity || 0;
            if (count <= 0) {
              return;
            }
            const restaurantCount = slot?.restaurantQuantity || 0;
            const itemKey = packingItemKey(meal.id, shiftId);
            items.push({
              key: itemKey,
              mealId: meal.id,
              shiftId,
              name: meal.name,
              count,
              badges: restaurantCount > 0 ? [this.$t("report_mobile.restaurant_ordered", { n: restaurantCount })] : [],
              packed: Boolean(saved[itemKey]),
            });
          });
        });

      const groups = SHIFTS.map((shift) => shift.id)
        .map((shiftId) => {
          const list = items.filter((item) => item.shiftId === shiftId);
          const total = list.reduce((sum, item) => sum + item.count, 0);
          const done = list.filter((item) => item.packed).reduce((sum, item) => sum + item.count, 0);
          return { id: shiftId, items: list, total, done, complete: total > 0 && done === total };
        })
        .filter((group) => group.items.length);

      const total = groups.reduce((sum, group) => sum + group.total, 0);
      const done = groups.reduce((sum, group) => sum + group.done, 0);
      const complete = total > 0 && done === total;

      return {
        key,
        source: company,
        name: this.toLatin(company.name),
        groups,
        total,
        done,
        complete,
        ready: complete && Boolean(state.ready),
        percent: total ? Math.round((done / total) * 100) : 0,
        shiftIds: groups.map((group) => group.id),
      };
    },
    matches(text, search) {
      return this.customSearch([{ text }], [{ value: "text" }], search).length > 0;
    },
    togglePacked(companyKey, itemKey) {
      const state = this.packingState[companyKey] || { items: {}, ready: false };
      const items = { ...state.items, [itemKey]: !state.items?.[itemKey] };
      this.updatePackingState(companyKey, { items, ready: state.ready && items[itemKey] });
    },
    setReady(companyKey, ready) {
      const state = this.packingState[companyKey] || { items: {}, ready: false };
      this.updatePackingState(companyKey, { ...state, ready });
      if (ready) {
        this.onCompanyReady(companyKey);
      }
    },
    updatePackingState(companyKey, companyState) {
      this.packingState = { ...this.packingState, [companyKey]: companyState };
      writePackingState(this.storageKey, this.packingState);
      // Značka "Pakovanje" u meniju ljuske prati izmene
      this.$root.$emit("report-packing-changed");
    },
    readPackingState() {
      return readPackingState(this.storageKey);
    },
    /** Kuke za ekran: stanje je učitano za novi ključ / firma je označena kao spremna. */
    onPackingStateReset() {},
    onCompanyReady() {},
  },
};
